import { useState, useEffect, useRef } from "react";
import { X, RefreshCw, Sparkles } from "lucide-react";
import serviceWorkerManager from "../lib/ServiceWorkerManager.js";
import versionManager from "../lib/VersionManager.js";
import { useThemeColors } from "../hooks/useThemeColors.js";

const UpdatePrompt = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [updateType, setUpdateType] = useState("normal"); // 'normal' or 'critical'
  const [versionInfo, setVersionInfo] = useState(null);
  const { themeColor } = useThemeColors();

  // Add refs to prevent multiple clicks
  const isProcessingRef = useRef(false);
  const lastClickTimeRef = useRef(0);

  useEffect(() => {
    // Listen for service worker updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleServiceWorkerUpdate = () => {
      // Don't show if already processing an update
      if (isProcessingRef.current || isApplying) {
        return;
      }

      // Double-check that there's actually an update available
      const { updateAvailable, registration } =
        serviceWorkerManager.getUpdateStatus();
      if (updateAvailable && registration && registration.waiting) {
        setUpdateType("normal");
        setIsVisible(true);
      } else {
      }
    };

    // Listen for version updates (for cached users)
    const handleVersionUpdate = (event) => {
      // Don't show if already processing an update
      if (isProcessingRef.current || isApplying) {
        return;
      }

      setUpdateType("critical");
      setVersionInfo(event.detail);
      setIsVisible(true);
    };

    // Register event listeners
    serviceWorkerManager.onUpdateAvailable(handleServiceWorkerUpdate);
    window.addEventListener("pwa-version-update", handleVersionUpdate);

    // Check if update is already available (with validation)
    const { updateAvailable, registration } =
      serviceWorkerManager.getUpdateStatus();
    if (updateAvailable && registration && registration.waiting) {
      setUpdateType("normal");
      setIsVisible(true);
    } else {
    }

    // Check for version updates (with validation)
    if (versionManager.isUpdateAvailable()) {
      setUpdateType("critical");
      setIsVisible(true);
    } else {
    }

    return () => {
      window.removeEventListener("pwa-version-update", handleVersionUpdate);
    };
  }, []);

  const handleApplyUpdate = async (event) => {
    // Prevent multiple clicks and touch events
    if (isProcessingRef.current || isApplying) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Debounce rapid clicks (prevent double-tap on mobile)
    const now = Date.now();
    if (now - lastClickTimeRef.current < 1000) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    lastClickTimeRef.current = now;

    // Set processing flag and hide prompt immediately
    isProcessingRef.current = true;
    setIsApplying(true);
    setIsVisible(false); // Hide prompt immediately to prevent double-trigger

    try {
      if (updateType === "critical") {
        // Force update for cached users
        await versionManager.forceUpdate();
      } else {
        // Normal service worker update
        await serviceWorkerManager.applyUpdate();
      }
    } catch (error) {
      // If update fails, try to reload anyway
      window.location.reload();
    }
  };

  const handleSkipUpdate = () => {
    serviceWorkerManager.skipUpdate();
    setIsVisible(false);
  };

  // Debug function to check update status
  const debugUpdateStatus = () => {
    const swStatus = serviceWorkerManager.getUpdateStatus();
    const versionStatus = versionManager.isUpdateAvailable();
  };

  // Add debug function to window for console access
  useEffect(() => {
    window.debugUpdateStatus = debugUpdateStatus;
    return () => {
      delete window.debugUpdateStatus;
    };
  }, []);

  if (!isVisible) return null;

  // Light background color for icon (20% opacity)
  const iconBgColor = themeColor ? `${themeColor}33` : "#3B82F620";
  // Hover color for button (darker, 85% opacity)
  const hoverColor = themeColor ? `${themeColor}dd` : "#2563EB";

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: iconBgColor }}
            >
              <Sparkles
                className="w-4 h-4"
                style={{ color: themeColor || "#3B82F6" }}
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">
              New Update Available
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {updateType === "critical"
                ? "We've made some improvements. Click to update and enjoy the latest features."
                : "A new version is ready with improvements and bug fixes."}
            </p>
          </div>

          <button
            onClick={handleSkipUpdate}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleSkipUpdate();
            }}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors touch-manipulation"
            style={{
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
            }}
            aria-label="Dismiss update prompt"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleApplyUpdate}
            onTouchEnd={(e) => {
              // Prevent default touch behavior and handle as click
              e.preventDefault();
              handleApplyUpdate(e);
            }}
            disabled={isApplying}
            className="flex-1 text-white text-sm font-medium py-2 px-3 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 touch-manipulation"
            style={{
              backgroundColor: themeColor || "#3B82F6",
              WebkitTouchCallout: "none",
              WebkitUserSelect: "none",
              userSelect: "none",
            }}
            onMouseEnter={(e) => {
              if (!isApplying) {
                e.currentTarget.style.backgroundColor = hoverColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isApplying) {
                e.currentTarget.style.backgroundColor = themeColor || "#3B82F6";
              }
            }}
          >
            {isApplying ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Click to Update
              </>
            )}
          </button>

          {updateType === "normal" && (
            <button
              onClick={handleSkipUpdate}
              onTouchEnd={(e) => {
                e.preventDefault();
                handleSkipUpdate();
              }}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors touch-manipulation"
              style={{
                WebkitTouchCallout: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
              }}
            >
              Later
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdatePrompt;
